import type { QueryClient } from '@tanstack/react-query';
import { ApiError } from '../../lib/api-client';
import { useSession } from '../../stores/session';
import { SESSION_QUERY_KEY } from './api/auth-api';

/** `?reason=` on the sign-in screen, so it can say why the person is there. */
export const SESSION_ENDED_REASON = 'session_ended';

/**
 * A 401 on any request while signed in means the session ended underneath the
 * screen — expired, signed out elsewhere, or revoked. Without this, the screen that
 * made the request showed "Something went wrong at our end" (S0–S4 audit, item 4).
 *
 * Nothing happens while signed out: a 401 there is a wrong password, not an ended
 * session.
 */
export function handleSessionEnded(
  error: unknown,
  queryClient: QueryClient,
  goToSignIn: () => Promise<void>,
): void {
  if (!(error instanceof ApiError) || !error.isUnauthenticated) return;
  if (useSession.getState().session === null) return;

  useSession.getState().setSession(null);
  // Set before navigating: the sign-in route's gate reads this cache entry, and
  // the old session still in it would bounce the redirect straight back.
  queryClient.setQueryData(SESSION_QUERY_KEY, null);

  void goToSignIn().then(() => {
    // The next person to sign in on this browser must not see this one's data.
    queryClient.removeQueries({
      predicate: (query) => query.queryKey[0] !== SESSION_QUERY_KEY[0],
    });
  });
}

const RETRYABLE_CLIENT_ERRORS: ReadonlySet<number> = new Set([408, 429]);

/** A refused or missing record answers the same way every time; retrying only holds the skeleton on screen. */
export function shouldRetry(failureCount: number, error: unknown): boolean {
  // 408 (timed out) and 429 (too many requests) can succeed a moment later; every other 4xx can't.
  if (error instanceof ApiError && error.status >= 400 && error.status < 500 && !RETRYABLE_CLIENT_ERRORS.has(error.status)) return false;
  return failureCount < 1;
}
